import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ShieldCheck, CheckCircle2, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Eyebrow } from "@/components/landing/Eyebrow";
import { publicApi } from "@/lib/api";
import { WEBINAR, SCROLL_REVEAL, HERO_BULLETS, formatWebinarDate } from "@/lib/content";

const INITIAL_FORM = {
  name: "",
  whatsapp: "",
  email: "",
  industry: "",
  bottleneck: "",
};

export const RegistrationForm = ({ settings, dropdowns, onRegistered, formRef }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const remaining = settings?.seats_remaining ?? "—";
  const total = settings?.total_seats ?? "—";

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "請輸入您的姓名";
    if (!form.whatsapp.trim()) e.whatsapp = "請輸入 WhatsApp 號碼";
    else if (!/^\+?[\d\s\-()]{6,25}$/.test(form.whatsapp.trim()))
      e.whatsapp = "請輸入有效的 WhatsApp 號碼";
    if (!form.email.trim()) e.email = "請輸入電郵地址";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      e.email = "請輸入有效的電郵地址";
    if (!form.industry.trim()) e.industry = "請輸入您的行業";
    if (!form.bottleneck) e.bottleneck = "請選擇最大瓶頸";
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length > 0) {
      toast.error("請檢查表單欄位", { description: "請補全所有必填資訊。" });
      return;
    }
    try {
      setSubmitting(true);
      await publicApi.register({
        name: form.name.trim(),
        whatsapp: form.whatsapp.trim(),
        email: form.email.trim(),
        industry: form.industry.trim(),
        bottleneck: form.bottleneck,
      });
      toast.success("已為您保留席位", {
        description: "正在跳轉至 WhatsApp 社群⋯⋯",
      });
      setSubmitted(true);
      setForm(INITIAL_FORM);
      onRegistered?.();
      // Redirect to WhatsApp group — use same-tab navigation to avoid popup-blocker
      const waUrl = settings?.whatsapp_redirect_url;
      const isValidUrl =
        waUrl &&
        typeof waUrl === "string" &&
        waUrl.trim() &&
        waUrl.trim() !== "https://chat.whatsapp.com/" &&
        /^https?:\/\//.test(waUrl.trim());
      if (isValidUrl) {
        // Short delay so the success toast / panel becomes visible briefly,
        // then navigate in the same tab (works on mobile + desktop, not blocked).
        setTimeout(() => {
          window.location.href = waUrl.trim();
        }, 300);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let message = "提交未成功，請稍後再試";
      if (typeof detail === "string") message = detail;
      else if (Array.isArray(detail) && detail[0]?.msg) message = detail[0].msg;
      toast.error("報名未成功", { description: message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="register"
      ref={formRef}
      data-testid="registration-form-section"
      className="relative bg-ink py-14 sm:py-24 lg:py-28"
    >
      {/* Intensified spotlight */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute inset-0 spotlight-small" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1200px] h-[800px] rounded-full opacity-40 blur-[120px]" style={{ background: "radial-gradient(circle, rgba(212, 180, 106,0.18), transparent 65%)" }} />
      </div>
      <div className="absolute top-0 left-0 right-0 hairline-h" aria-hidden />
      <div className="relative container px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        <motion.div {...SCROLL_REVEAL} className="text-center">
          <div className="flex justify-center">
            <Eyebrow label="BEGIN" sub="NOW" />
          </div>
          <h2 className="font-serif-tc mt-5 sm:mt-7 text-[32px] leading-[1.1] sm:text-5xl lg:text-6xl font-bold tracking-[-0.02em]">
            2 分鐘鎖定<span className="lime-keyword">免費席位</span>
          </h2>
          <p className="mt-3 sm:mt-5 text-[13px] sm:text-base leading-relaxed text-[hsl(var(--muted-foreground))] max-w-[54ch] mx-auto font-num">
            {formatWebinarDate(settings?.webinar_date, "long", WEBINAR.dateLong)} · {settings?.webinar_time || "20:00"}（港）· Zoom · 2 小時
          </p>
        </motion.div>

        {/* Split layout: left = takeaways, right = form */}
        <div className="mt-8 sm:mt-12 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
          {/* Left: what you'll get */}
          <motion.aside
            {...SCROLL_REVEAL}
            className="lg:col-span-5 lg:sticky lg:top-24 order-2 lg:order-1"
          >
            <div className="card-dark p-5 sm:p-7">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--lime))]">
                <Sparkles className="w-3 h-3" />
                2 小時內你將帶走
              </div>
              <ul className="mt-5 space-y-3.5">
                {HERO_BULLETS.map((x, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-0.5 w-6 h-6 rounded-full bg-[rgba(212, 180, 106,0.12)] border border-[rgba(212, 180, 106,0.35)] flex items-center justify-center flex-shrink-0 font-num text-[11px] font-bold text-[hsl(var(--lime))]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-[13px] sm:text-[14px] text-[hsl(var(--foreground))]/92 leading-relaxed pt-0.5">
                      {x}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-5 border-t border-[rgba(255,255,255,0.06)] text-[12px] text-[hsl(var(--muted-foreground))] leading-relaxed">
                名額有限・先到先得。報名後將獲得 Zoom 連結及提醒。
              </div>
            </div>
          </motion.aside>

          {/* Right: form */}
          <motion.div
            {...SCROLL_REVEAL}
            className="lg:col-span-7 card-dark p-5 sm:p-8 lg:p-9 order-1 lg:order-2"
          >
          <div className="flex flex-wrap items-center justify-between gap-3 pb-5 border-b border-[rgba(255,255,255,0.06)]">
            <div className="flex items-center gap-2 text-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--lime))] animate-pulse" />
              <span className="text-[hsl(var(--foreground))]/85">名額剩餘</span>
              <span className="font-num font-bold text-[hsl(var(--lime))]">{remaining}</span>
              <span className="text-[hsl(var(--muted-foreground))]">/ {total}</span>
            </div>
            {settings?.show_original_price && (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-[hsl(var(--muted-foreground))]">原價</span>
                <span className="font-num line-through text-[hsl(var(--muted-foreground))]">
                  HKD {settings.original_price_hkd.toLocaleString()}
                </span>
                <span className="font-semibold text-[hsl(var(--lime))] uppercase tracking-wider">限時免費</span>
              </div>
            )}
          </div>

          {submitted ? (
            <div data-testid="registration-success-panel" className="py-8 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-[rgba(212, 180, 106,0.12)] border border-[rgba(212, 180, 106,0.4)] flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-[hsl(var(--lime))]" />
              </div>
              <h3 className="font-serif-tc mt-4 text-xl sm:text-2xl font-bold">席位已鎖定</h3>
              <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] max-w-md mx-auto leading-relaxed">
                請留意電郵與 WhatsApp 通知。我們會在活動前 48 小時發送 Zoom 連結。
              </p>
              <Button
                onClick={() => setSubmitted(false)}
                className="btn-outline-soft mt-6 h-11 px-5"
              >
                為另一位朋友報名
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="text-sm font-medium text-[hsl(var(--foreground))]">姓名</Label>
                  <Input
                    id="name"
                    data-testid="webinar-registration-name-input"
                    type="text"
                    placeholder="請輸入您的姓名"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-2 bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.12)] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]/60 focus-visible:ring-[hsl(var(--lime))] rounded-xl h-11"
                  />
                  {errors.name && <p className="mt-1 text-xs text-[#F87171]">{errors.name}</p>}
                </div>
                <div>
                  <Label htmlFor="whatsapp" className="text-sm font-medium text-[hsl(var(--foreground))]">WhatsApp 號碼</Label>
                  <Input
                    id="whatsapp"
                    data-testid="webinar-registration-whatsapp-input"
                    type="tel"
                    placeholder="+852 xxxx xxxx"
                    value={form.whatsapp}
                    onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                    className="mt-2 bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.12)] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]/60 focus-visible:ring-[hsl(var(--lime))] rounded-xl h-11"
                  />
                  {errors.whatsapp && <p className="mt-1 text-xs text-[#F87171]">{errors.whatsapp}</p>}
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-sm font-medium text-[hsl(var(--foreground))]">電郵</Label>
                <Input
                  id="email"
                  data-testid="webinar-registration-email-input"
                  type="email"
                  placeholder="name@example.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-2 bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.12)] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]/60 focus-visible:ring-[hsl(var(--lime))] rounded-xl h-11"
                />
                {errors.email && <p className="mt-1 text-xs text-[#F87171]">{errors.email}</p>}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="industry" className="text-sm font-medium text-[hsl(var(--foreground))]">您目前的行業</Label>
                  <Input
                    id="industry"
                    data-testid="webinar-registration-industry-input"
                    type="text"
                    placeholder="例：教育、財務顧問、設計師⋯⋯"
                    value={form.industry}
                    onChange={(e) => setForm({ ...form, industry: e.target.value })}
                    className="mt-2 bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.12)] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]/60 focus-visible:ring-[hsl(var(--lime))] rounded-xl h-11"
                  />
                  {errors.industry && <p className="mt-1 text-xs text-[#F87171]">{errors.industry}</p>}
                </div>
                <div>
                  <Label className="text-sm font-medium text-[hsl(var(--foreground))]">個人 IP 最大瓶頸</Label>
                  <Select value={form.bottleneck} onValueChange={(v) => setForm({ ...form, bottleneck: v })}>
                    <SelectTrigger
                      data-testid="webinar-registration-bottleneck-select"
                      className="mt-2 bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.12)] text-[hsl(var(--foreground))] focus:ring-[hsl(var(--lime))] rounded-xl h-11"
                    >
                      <SelectValue placeholder="請選擇瓶頸" />
                    </SelectTrigger>
                    <SelectContent>
                      {(dropdowns?.bottlenecks || []).map((x) => (
                        <SelectItem key={x} value={x}>{x}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.bottleneck && <p className="mt-1 text-xs text-[#F87171]">{errors.bottleneck}</p>}
                </div>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                data-testid="webinar-registration-submit-button"
                size="lg"
                className="btn-lime w-full h-12 text-base"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> 提交中…
                  </>
                ) : (
                  <>
                    {WEBINAR.ctaCopy}
                    <ArrowRight className="w-4 h-4 ml-1.5" />
                  </>
                )}
              </Button>

              <p className="flex items-center justify-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))]">
                <ShieldCheck className="w-3.5 h-3.5" />
                資料 100% 保密 · 無任何費用 · 確認通知會寄送
              </p>
            </form>
          )}
        </motion.div>
        </div>
      </div>
    </section>
  );
};
